<template>
  <div class="gl-login">
    <!-- Left brand panel -->
    <div class="gl-login__brand">
      <div class="gl-login__brand-inner">
        <div class="gl-login__brand-logo">
          <span class="gl-login__brand-mark">GL</span>
          <span class="gl-login__brand-name">GetLokal</span>
        </div>

        <div class="gl-login__brand-headline">
          <h1>Run your food business smarter.</h1>
          <p>One platform for orders, inventory, staff, delivery, and growth — all in one place.</p>
        </div>

        <ul class="gl-login__features">
          <li>
            <span class="gl-login__feat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </span>
            Multi-branch management
          </li>
          <li>
            <span class="gl-login__feat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </span>
            Real-time order tracking
          </li>
          <li>
            <span class="gl-login__feat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </span>
            Smart inventory & stock control
          </li>
          <li>
            <span class="gl-login__feat-icon">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
            </span>
            Loyalty rewards engine
          </li>
        </ul>

        <div class="gl-login__brand-footer">
          © {{ new Date().getFullYear() }} GetLokal Sdn Bhd
        </div>
      </div>

      <!-- Decorative background shapes -->
      <div class="gl-login__deco gl-login__deco--1"></div>
      <div class="gl-login__deco gl-login__deco--2"></div>
      <div class="gl-login__deco gl-login__deco--3"></div>
    </div>

    <!-- Right form panel -->
    <div class="gl-login__form-panel">
      <div class="gl-login__form-wrap">

        <!-- Mobile-only logo -->
        <div class="gl-login__mobile-logo">
          <span class="gl-login__brand-mark">GL</span>
          <span class="gl-login__brand-name">GetLokal</span>
        </div>

        <div class="gl-login__form-header">
          <h2>Welcome back</h2>
          <p>Sign in to your GetLokal admin account.</p>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="gl-login__error" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          <span>{{ error }}</span>
          <button type="button" class="gl-login__error-close" @click="error = null">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form class="gl-login__form" @submit.prevent="doLogin" novalidate>
          <div class="gl-form-field">
            <label for="login-email">Email address</label>
            <div class="gl-input-wrap">
              <span class="gl-input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
              </span>
              <input
                id="login-email"
                v-model="email"
                type="email"
                class="gl-input"
                placeholder="admin@getlokal.my"
                autocomplete="email"
                required
                :disabled="loading"
              />
            </div>
          </div>

          <div class="gl-form-field">
            <label for="login-password">Password</label>
            <div class="gl-input-wrap">
              <span class="gl-input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
              </span>
              <input
                id="login-password"
                v-model="password"
                :type="passwordType"
                class="gl-input"
                placeholder="••••••••"
                autocomplete="current-password"
                required
                :disabled="loading"
              />
              <button type="button" class="gl-input-toggle" @click="togglePassword" tabindex="-1">
                <svg v-if="passwordType === 'password'" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>
              </button>
            </div>
          </div>

          <div class="gl-login__options">
            <label class="gl-checkbox">
              <input type="checkbox" v-model="rememberMe" />
              <span class="gl-checkbox__box"></span>
              <span class="gl-checkbox__label">Remember me</span>
            </label>
          </div>

          <button type="submit" class="gl-login__submit" :disabled="loading">
            <span v-if="!loading">Sign in</span>
            <span v-else class="gl-login__loading">
              <svg class="gl-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
              Signing in…
            </span>
          </button>
        </form>

        <p class="gl-login__register">
          New business?
          <router-link to="/auth/owner-register">Register your company →</router-link>
        </p>

        <!-- Dev quick login -->
        <div v-if="isDev" class="gl-login__dev">
          <span class="gl-login__dev-label">Dev Quick Login</span>
          <div class="gl-login__dev-btns">
            <button type="button" @click="quickLogin('superadmin')" :disabled="loading">
              Super Admin
            </button>
            <button type="button" @click="quickLogin('owner')" :disabled="loading">
              Business Owner
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth';
import { mapState, mapActions } from 'pinia';

export default {
  name: 'LoginPage',

  data() {
    return {
      email: '',
      password: '',
      passwordType: 'password',
      rememberMe: false,
      error: null,
      loading: false
    };
  },

  computed: {
    ...mapState(useAuthStore, ['isAuthenticated']),
    isDev() {
      return process.env.NODE_ENV === 'development' || process.env.VUE_APP_DEV_MODE === 'true';
    }
  },

  created() {
    if (this.isAuthenticated) {
      this.$router.replace('/');
    }
  },

  methods: {
    ...mapActions(useAuthStore, ['login']),

    togglePassword() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    },

    async doLogin() {
      if (!this.email || !this.password) {
        this.error = 'Please enter your email and password.';
        return;
      }

      this.loading = true;
      this.error = null;

      const result = await this.login(this.email, this.password);

      if (result.success) {
        this.$toast.show('Login successful!', {
          theme: 'outline',
          position: 'top-right',
          icon: 'check',
          type: 'success',
          duration: 2000
        });
        this.$router.replace('/');
      } else {
        this.error = result.message;
      }

      this.loading = false;
    },

    async quickLogin(type) {
      const credentials = {
        superadmin: { email: 'superadmin@getlokal.my', password: 'SuperAdmin123!' },
        owner:      { email: 'mussyahmi31@gmail.com', password: 'Owner123!' }
      };
      const cred = credentials[type];
      if (!cred) return;
      this.email = cred.email;
      this.password = cred.password;
      await this.doLogin();
    }
  }
};
</script>

<style scoped>
/* ─── LAYOUT ─────────────────────────────────────────────────── */
.gl-login {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
}

/* ─── LEFT BRAND PANEL ────────────────────────────────────────── */
.gl-login__brand {
  position: relative;
  background: #900C27;
  background: linear-gradient(145deg, #900C27 0%, #6f0a1e 55%, #4a0714 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 52px;
  overflow: hidden;
}

.gl-login__brand-inner {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 36px;
  max-width: 440px;
  width: 100%;
}

/* Brand logo */
.gl-login__brand-logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.gl-login__brand-mark {
  width: 42px;
  height: 42px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 15px;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.gl-login__brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
}

/* Headline */
.gl-login__brand-headline h1 {
  font-size: 32px;
  font-weight: 800;
  color: #fff;
  line-height: 1.2;
  letter-spacing: -0.8px;
  margin: 0 0 12px;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
}

.gl-login__brand-headline p {
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.6;
  margin: 0;
}

/* Features list */
.gl-login__features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gl-login__features li {
  display: flex;
  align-items: center;
  gap: 12px;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  font-weight: 500;
}

.gl-login__feat-icon {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gl-login__feat-icon svg {
  width: 12px;
  height: 12px;
  color: #fff;
  stroke: #fff;
}

/* Footer */
.gl-login__brand-footer {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.38);
  margin-top: auto;
  padding-top: 8px;
}

/* Decorative shapes */
.gl-login__deco {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  pointer-events: none;
}

.gl-login__deco--1 {
  width: 360px;
  height: 360px;
  bottom: -100px;
  right: -100px;
}

.gl-login__deco--2 {
  width: 200px;
  height: 200px;
  top: -60px;
  right: -40px;
  background: rgba(255, 255, 255, 0.07);
}

.gl-login__deco--3 {
  width: 120px;
  height: 120px;
  top: 40%;
  left: -40px;
  background: rgba(255, 255, 255, 0.04);
}

/* ─── RIGHT FORM PANEL ────────────────────────────────────────── */
.gl-login__form-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 52px;
  background: #ffffff;
}

.gl-login__form-wrap {
  width: 100%;
  max-width: 400px;
}

/* Mobile logo (hidden on desktop) */
.gl-login__mobile-logo {
  display: none;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  justify-content: center;
}

.gl-login__mobile-logo .gl-login__brand-mark {
  background: #900C27;
  border: none;
  font-size: 14px;
  width: 38px;
  height: 38px;
}

.gl-login__mobile-logo .gl-login__brand-name {
  font-size: 19px;
  color: #111318;
}

/* Form header */
.gl-login__form-header {
  margin-bottom: 28px;
}

.gl-login__form-header h2 {
  font-size: 26px;
  font-weight: 800;
  color: #111318;
  letter-spacing: -0.6px;
  margin: 0 0 6px;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
}

.gl-login__form-header p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Error alert */
.gl-login__error {
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 13.5px;
  color: #b91c1c;
  font-weight: 500;
}

.gl-login__error svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }

.gl-login__error-close {
  margin-left: auto;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #ef4444;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.gl-login__error-close svg { width: 14px; height: 14px; }

/* Form */
.gl-login__form { display: flex; flex-direction: column; gap: 18px; }

.gl-form-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.gl-form-field label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
}

.gl-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.gl-input-icon {
  position: absolute;
  left: 13px;
  z-index: 1;
  pointer-events: none;
  display: flex;
  align-items: center;
}

.gl-input-icon svg {
  width: 17px;
  height: 17px;
  stroke: #9ca3af;
}

.gl-input {
  width: 100%;
  height: 46px;
  padding: 0 44px;
  border: 1.5px solid #d1d5db;
  border-radius: 10px;
  font-size: 14px;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
  color: #111318;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  outline: none;
}

.gl-input:focus {
  border-color: #900C27;
  box-shadow: 0 0 0 3px rgba(144, 12, 39, 0.10);
}

.gl-input::placeholder { color: #9ca3af; }
.gl-input:disabled { background: #f9fafb; color: #9ca3af; }

.gl-input-toggle {
  position: absolute;
  right: 13px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: #9ca3af;
  transition: color 0.15s ease;
}

.gl-input-toggle:hover { color: #6b7280; }
.gl-input-toggle svg { width: 17px; height: 17px; }

/* Options row */
.gl-login__options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -4px;
}

.gl-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}

.gl-checkbox input[type="checkbox"] { display: none; }

.gl-checkbox__box {
  width: 17px;
  height: 17px;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  transition: all 0.15s ease;
  flex-shrink: 0;
  position: relative;
}

.gl-checkbox input:checked ~ .gl-checkbox__box {
  background: #900C27;
  border-color: #900C27;
}

.gl-checkbox input:checked ~ .gl-checkbox__box::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 7px;
  height: 11px;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.gl-checkbox__label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

/* Submit button */
.gl-login__submit {
  width: 100%;
  height: 48px;
  background: #900C27;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
  letter-spacing: 0.1px;
}

.gl-login__submit:hover {
  background: #6f0a1e;
  box-shadow: 0 6px 20px rgba(144, 12, 39, 0.35);
  transform: translateY(-1px);
}

.gl-login__submit:active { transform: translateY(0); }

.gl-login__submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Loading spinner */
.gl-login__loading {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gl-spin {
  width: 18px;
  height: 18px;
  animation: gl-spin 0.8s linear infinite;
}

@keyframes gl-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* Register link */
.gl-login__register {
  text-align: center;
  font-size: 13.5px;
  color: #6b7280;
  margin-top: 22px;
  margin-bottom: 0;
}

.gl-login__register a {
  color: #900C27;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s ease;
}

.gl-login__register a:hover { color: #6f0a1e; text-decoration: underline; }

/* Dev quick login */
.gl-login__dev {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px dashed #e5e7eb;
}

.gl-login__dev-label {
  display: block;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
  margin-bottom: 12px;
}

.gl-login__dev-btns {
  display: flex;
  gap: 8px;
}

.gl-login__dev-btns button {
  flex: 1;
  height: 36px;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  font-family: 'Plus Jakarta Sans', 'Montserrat', sans-serif;
  cursor: pointer;
  border: 1.5px solid #e5e7eb;
  background: #f9fafb;
  color: #374151;
  transition: all 0.15s ease;
}

.gl-login__dev-btns button:first-child:hover {
  border-color: #fca5a5;
  background: #fef2f2;
  color: #b91c1c;
}

.gl-login__dev-btns button:last-child:hover {
  border-color: #900C27;
  background: #fef2f2;
  color: #900C27;
}

.gl-login__dev-btns button:disabled { opacity: 0.5; cursor: not-allowed; }

/* ─── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 900px) {
  .gl-login {
    grid-template-columns: 1fr;
  }

  .gl-login__brand {
    display: none;
  }

  .gl-login__form-panel {
    padding: 40px 24px;
    align-items: flex-start;
    padding-top: 60px;
  }

  .gl-login__mobile-logo {
    display: flex;
  }

  .gl-login__form-wrap {
    max-width: 420px;
    margin: 0 auto;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .gl-login__form-panel {
    padding: 36px 20px;
  }

  .gl-login__form-header h2 { font-size: 22px; }
}
</style>
